var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import React, { useState, useCallback, useEffect, useMemo, } from "react";
import { Pane, Button, ConfirmationModal, MenuSection, MessageBanner, TextLink, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useMutation, useQueryClient } from "react-query";
import { useIntl, FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";
import NavComponent from "../../components/NavComponent";
import TableComponent from "../../components/TableComponent";
import { PeriodicalFilters } from "../../components/PeriodicalFilters";
import { StatusBadgeList } from "../../components/common/StatusBadgeList";
import { RecekPaneSet } from "../../components/RecekPaneSet";
import { createStripesSource } from "../../utils/createStripesSource";
import { formatCountSubtitle } from "../../utils/paneCount";
import { frequencyLabelId } from "../../utils/frequencyLabel";
import { formatDate } from "../../utils/formatUtils";
import { LAST_RECEIVED_ERROR_CODES } from "../../utils/lastReceivedFilter";
import { PeriodicalLibraryStatusEnum, } from "../../types/Api";
import { ProvisionalBadge } from "../../components/common/ProvisionalBadge";
import { useSubscribedPeriodicalsData } from "../../hooks/useSubscribedPeriodicalsData";
import { useTitleSort } from "../../hooks/useTitleSort";
import { useListErrorMessage } from "../../hooks/useListErrorMessage";
import { columnSortKeys } from "../../utils/titleSort";
import { usePeriodicalFilters } from "../../hooks/usePeriodicalFilters";
import { usePublishersData } from "../../hooks/usePublishersData";
import { PeriodicalDetailPane } from "../../components/periodical/PeriodicalDetailPane";
import { useVariantTitlesColumn, VARIANT_TITLES_COLUMN_WIDTH, } from "../../hooks/useVariantTitlesColumn";
import { PeriodicalEnforcementDialog } from "../../components/periodical/PeriodicalEnforcementDialog";
import { describeSendResult, summarizeSkippedRecords, } from "../../utils/urgenceSkipSummary";
import { ScheduleEditDialog } from "../../components/schedule/ScheduleEditDialog";
import { endPublication, reopenPublication, } from "../../api/periodicalIssueService";
import { PERIODICAL_LIBRARY_DETAIL_KEY, usePeriodicalLibraryActions, } from "../../hooks/usePeriodicalLibraryActions";
import { invalidateVolumeViews } from "../../hooks/usePeriodicalVolumes";
import { invalidateDepositRecordViews } from "../../utils/depositRecordQueries";
import { ROUTE_PATHS } from "../../types/routePaths";
import { PeriodicalCheckSettingsDialog } from "../../components/periodical/PeriodicalCheckSettingsDialog";
import { PeriodicalKultSubjectGroupDialog } from "../../components/periodical/PeriodicalKultSubjectGroupDialog";
import { checkSettingsOf, } from "../../types/periodicalCheck";
import { GenerateVolumeDialog } from "../../components/periodical/GenerateVolumeDialog";
import { NameCell } from "../../components/common/NameCell";
import { AgendaBadges } from "../../components/agenda/AgendaBadges";
import { ChangeAgendaDialog, } from "../../components/agenda/ChangeAgendaDialog";
import { useTitleAgenda } from "../../hooks/useAgenda";
import { handledElsewhere } from "../../utils/agendaLabel";
import { TitleAgendaDialog } from "../../components/agenda/TitleAgendaDialog";
import { useIsNationalLibrary } from "../../hooks/useIsNationalLibrary";
import { AGENDA_STATE_LIST_ERRORS, } from "../../types/agenda";
import css from "./PeriodicalsManagementList.module.css";
var ACTIVE_LIBRARY_LOOKUP_FAILED = "ACTIVE_LIBRARY_LOOKUP_FAILED";
// without defaults every column got the same share, which left the name a few letters wide next to the detail pane
var COLUMN_WIDTHS = {
    title: "24%",
    partNumber: "6%",
    partName: "9%",
    variantTitles: VARIANT_TITLES_COLUMN_WIDTH,
    issn: "10%",
    publisherName: "13%",
    frequency: "8%",
    status: "10%",
    lastReceived: "8%",
};
var LIST_ERROR_MESSAGE_IDS = __assign(__assign(__assign({}, Object.fromEntries(LAST_RECEIVED_ERROR_CODES.map(function (code) { return [
    code,
    "ui-br-legaldeposits.periodicals.lastReceived.error.".concat(code),
]; }))), (_a = {}, _a[ACTIVE_LIBRARY_LOOKUP_FAILED] = "ui-br-legaldeposits.field.error.".concat(ACTIVE_LIBRARY_LOOKUP_FAILED), _a)), AGENDA_STATE_LIST_ERRORS);
var PeriodicalsManagementList = function () {
    var _a, _b, _c;
    var formatMessage = useIntl().formatMessage;
    var history = useHistory();
    var _d = useState(true), filterPaneIsVisible = _d[0], setFilterPaneIsVisible = _d[1];
    var _e = usePeriodicalLibraryActions(), selectedItem = _e.selectedItem, handleRowClick = _e.handleRowClick, handleCloseDetail = _e.handleCloseDetail;
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var _f = useState(false), scheduleDialogOpen = _f[0], setScheduleDialogOpen = _f[1];
    var _g = useState(false), endPubConfirmOpen = _g[0], setEndPubConfirmOpen = _g[1];
    var _h = useState(false), reopenConfirmOpen = _h[0], setReopenConfirmOpen = _h[1];
    var _j = useState(null), enforcementKind = _j[0], setEnforcementKind = _j[1];
    var _k = useState(null), enforcementResult = _k[0], setEnforcementResult = _k[1];
    var _l = useState(null), enforcementSkipped = _l[0], setEnforcementSkipped = _l[1];
    var _m = useState(false), generateNextVolumeOpen = _m[0], setGenerateNextVolumeOpen = _m[1];
    var _o = useState(false), checkSettingsOpen = _o[0], setCheckSettingsOpen = _o[1];
    var _p = useState(false), kultSubjectGroupDialogOpen = _p[0], setKultSubjectGroupDialogOpen = _p[1];
    var _q = useState(null), agendaDialogMode = _q[0], setAgendaDialogMode = _q[1];
    var _r = useState(null), agendaResult = _r[0], setAgendaResult = _r[1];
    var titleAgendaQuery = useTitleAgenda(selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.titleId);
    var readOnlyHere = handledElsewhere(selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.agenda, "PERIODICALS");
    var _s = useIsNationalLibrary(), isNationalLibrary = _s.isNationalLibrary, activeLibraryId = _s.activeLibraryId;
    var handleCloseAgendaDialog = useCallback(function () { return setAgendaDialogMode(null); }, []);
    var _t = useState(false), agendaOverviewOpen = _t[0], setAgendaOverviewOpen = _t[1];
    var handleOpenAgendaOverview = useCallback(function () { return setAgendaOverviewOpen(true); }, []);
    var handleCloseAgendaOverview = useCallback(function () { return setAgendaOverviewOpen(false); }, []);
    useEffect(function () {
        setAgendaOverviewOpen(false);
    }, [selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id]);
    var handleAgendaChanged = useCallback(function (result) {
        var _a, _b;
        var mode = agendaDialogMode;
        setAgendaDialogMode(null);
        setAgendaOverviewOpen(false);
        var name = (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.title) !== null && _a !== void 0 ? _a : "";
        var newAgenda = (_b = result.title) === null || _b === void 0 ? void 0 : _b.agenda;
        setAgendaResult(formatMessage({
            id: mode === "global"
                ? "ui-br-legaldeposits.agenda.result.global"
                : "ui-br-legaldeposits.agenda.result.".concat(newAgenda !== null && newAgenda !== void 0 ? newAgenda : "PERIODICALS"),
        }, { name: name }));
    }, [agendaDialogMode, selectedItem, formatMessage]);
    var endPublicationMutation = useMutation(function (titleId) { return endPublication(kyInstance, titleId); }, {
        onSuccess: function () {
            queryClient.invalidateQueries(["periodicalLibraries"]);
            setEndPubConfirmOpen(false);
            handleCloseDetail();
        },
    });
    var reopenPublicationMutation = useMutation(function (titleId) { return reopenPublication(kyInstance, titleId); }, {
        onSuccess: function () {
            queryClient.invalidateQueries(["periodicalLibraries"]);
            queryClient.invalidateQueries([PERIODICAL_LIBRARY_DETAIL_KEY]);
            invalidateVolumeViews(queryClient);
            invalidateDepositRecordViews(queryClient);
            setReopenConfirmOpen(false);
        },
    });
    var handleOpenSchedule = useCallback(function () { return setScheduleDialogOpen(true); }, []);
    var handleCloseSchedule = useCallback(function () { return setScheduleDialogOpen(false); }, []);
    var handleOpenEndPub = useCallback(function () { return setEndPubConfirmOpen(true); }, []);
    var handleCloseEndPub = useCallback(function () { return setEndPubConfirmOpen(false); }, []);
    var handleConfirmEndPub = useCallback(function () {
        if (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.titleId) {
            endPublicationMutation.mutate(selectedItem.titleId);
        }
    }, [endPublicationMutation, selectedItem]);
    var handleOpenReopen = useCallback(function () { return setReopenConfirmOpen(true); }, []);
    var handleCloseReopen = useCallback(function () { return setReopenConfirmOpen(false); }, []);
    var handleConfirmReopen = useCallback(function () {
        if (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.titleId) {
            reopenPublicationMutation.mutate(selectedItem.titleId);
        }
    }, [reopenPublicationMutation, selectedItem]);
    var handleOpenUrgence = useCallback(function () { return setEnforcementKind("URGENCE"); }, []);
    var handleOpenReminder = useCallback(function () { return setEnforcementKind("REMINDER"); }, []);
    var handleOpenDamagedClaim = useCallback(function () { return setEnforcementKind("DAMAGED_CLAIM"); }, []);
    var handleCloseEnforcement = useCallback(function () { return setEnforcementKind(null); }, []);
    // the dialog unmounts on close, so the outcome has to be reported by the list behind it
    var handleEnforcementResult = useCallback(function (result, kind) {
        if (kind === "DAMAGED_CLAIM") {
            return;
        }
        setEnforcementResult(describeSendResult(result, kind, formatMessage));
        setEnforcementSkipped(summarizeSkippedRecords(result.skipped, formatMessage));
    }, [formatMessage]);
    var handleOpenGenerateNextVolume = useCallback(function () { return setGenerateNextVolumeOpen(true); }, []);
    var handleCloseGenerateNextVolume = useCallback(function () { return setGenerateNextVolumeOpen(false); }, []);
    var isEnded = (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.periodicalActive) === false;
    var checkSettings = useMemo(function () {
        return selectedItem
            ? checkSettingsOf(selectedItem)
            : null;
    }, [selectedItem]);
    var handleOpenTakeOver = useCallback(function () { return setAgendaDialogMode("library"); }, []);
    var detailActionMenu = function (volumeAction) { return (React.createElement(MenuSection, { label: formatMessage({ id: "ui-br-legaldeposits.action.menu.label" }), id: "periodical-detail-actions" }, readOnlyHere ? (React.createElement(React.Fragment, null,
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenTakeOver, fullWidth: true, "data-test-id": "periodical-action-take-over" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.action.takeOver.PERIODICALS" })),
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenAgendaOverview, fullWidth: true, "data-test-id": "periodical-action-agenda-dialog" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.action.openDialog" })))) : (React.createElement(React.Fragment, null,
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenSchedule, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.edit-schedule" })),
        !isEnded && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenGenerateNextVolume, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.volume.generateNext" }))),
        volumeAction,
        isEnded ? (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenReopen, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.reopen-publication" }))) : (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenEndPub, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.end-publication" }))),
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () { return setCheckSettingsOpen(true); }, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.checkSettings" })),
        React.createElement(Button, { buttonStyle: "dropdownItem", fullWidth: true, onClick: function () { return setKultSubjectGroupDialogOpen(true); } },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.kultSubjectGroup.action" })),
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenReminder, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.send-reminder" })),
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenUrgence, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.send-urgence" })),
        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenDamagedClaim, fullWidth: true },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.action.send-damaged-claim" })),
        selectedItem && React.createElement("hr", null),
        selectedItem && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: handleOpenAgendaOverview, fullWidth: true, "data-test-id": "periodical-action-agenda-dialog" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.action.openDialog" }))))))); };
    var filtersHook = usePeriodicalFilters({
        lastReceived: true,
        agendaState: true,
    });
    var _u = usePublishersData(filtersHook.publisherQuery, filtersHook.filters.publisherId, "PERIODICAL_SUBSCRIBED"), publishers = _u.publishers, isPublishersLoading = _u.isPublishersLoading, selectedPublisher = _u.selectedPublisher;
    var toggleFilterPane = useCallback(function () { return setFilterPaneIsVisible(function (prev) { return !prev; }); }, []);
    var variantTitlesColumn = useVariantTitlesColumn();
    var columns = useMemo(function () { return [
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.title",
            }),
            key: "title",
            sortKey: "name",
            alwaysVisible: true,
            cellRenderer: function (item) {
                var _a;
                return (React.createElement(NameCell, null,
                    React.createElement(TextLink, { onClick: function (e) {
                            e.stopPropagation();
                            handleRowClick(item);
                        } }, item.title),
                    item.provisional && React.createElement(ProvisionalBadge, null),
                    React.createElement(AgendaBadges, { agenda: (_a = item.agenda) !== null && _a !== void 0 ? _a : "PERIODICALS", globalType: item.globalType, listedIn: "PERIODICALS" })));
            },
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.titles.table.partNumber",
            }),
            key: "partNumber",
            sortKey: "partNumber",
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.titles.table.partName",
            }),
            key: "partName",
            sortKey: "partName",
        },
        variantTitlesColumn,
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.issn",
            }),
            key: "issn",
            sortKey: "issn",
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.publisher",
            }),
            key: "publisherName",
            sortKey: "publisher",
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.frequency",
            }),
            key: "frequency",
            sortKey: "frequency",
            cellRenderer: function (item) {
                return item.frequency
                    ? formatMessage({ id: frequencyLabelId(item.frequency) })
                    : "";
            },
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.status",
            }),
            key: "status",
            cellRenderer: function (item) {
                var _a;
                return (React.createElement(StatusBadgeList, { statusesPerRecord: (_a = item.depositStatusesPerRecord) !== null && _a !== void 0 ? _a : [] }));
            },
        },
        {
            name: formatMessage({
                id: "ui-br-legaldeposits.periodicals.table.lastReceived",
            }),
            key: "lastReceived",
            sortKey: "lastReceived",
            cellRenderer: function (item) {
                if (item.lastReceived == null) {
                    return (React.createElement("span", { title: formatMessage({
                            id: "ui-br-legaldeposits.periodicals.table.lastReceived.unknown",
                        }) }, "\u2013"));
                }
                return item.lastReceived.date
                    ? formatDate(item.lastReceived.date)
                    : formatMessage({
                        id: "ui-br-legaldeposits.periodicals.table.lastReceived.never",
                    });
            },
        },
    ]; }, [formatMessage, handleRowClick, variantTitlesColumn]);
    var sortKeys = useMemo(function () { return columnSortKeys(columns); }, [columns]);
    var _v = useTitleSort("periodicalsManagement", sortKeys), sort = _v.sort, handleSort = _v.handleSort;
    var dataHook = useSubscribedPeriodicalsData(filtersHook.appliedFilters, sort);
    var errorMessage = useListErrorMessage(dataHook.error, LIST_ERROR_MESSAGE_IDS);
    var handleNeedMoreData = useCallback(function (offset) { return dataHook.fetchMoreData(offset); }, [dataHook]);
    var rowClassResolver = useCallback(function (item) {
        return item.periodicalActive === false ? css.endedRow : undefined;
    }, []);
    var handleGoToNewPeriodicals = useCallback(function () { return history.push(ROUTE_PATHS.NEW_PERIODICALS); }, [history]);
    var noSubscribedYet = !filtersHook.isAnyFilterActive() &&
        !dataHook.isLoading &&
        !dataHook.isError &&
        dataHook.totalRecords === 0;
    var emptyMessage = useMemo(function () {
        return noSubscribedYet ? (React.createElement("div", { className: css.emptyState },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.emptyState.message" }),
            React.createElement(Button, { onClick: handleGoToNewPeriodicals },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.emptyState.goToNewPeriodicals" })))) : undefined;
    }, [noSubscribedYet, handleGoToNewPeriodicals]);
    var source = useMemo(function () {
        return createStripesSource({
            ky: null,
            data: dataHook.periodicalsData,
            totalRecords: dataHook.totalRecords,
            isLoading: dataHook.isLoading,
            isError: dataHook.isError,
            errorMessage: errorMessage,
            isLoaded: true,
            fetchMore: function () {
                return dataHook.fetchMoreData(dataHook.pageOffset + dataHook.pageSize);
            },
        });
    }, [dataHook, errorMessage]);
    return (React.createElement(RecekPaneSet, null,
        filterPaneIsVisible && (React.createElement(Pane, { id: "menuPane", defaultWidth: "25%", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.pane.title.navigation",
            }) },
            React.createElement("div", { style: { display: "flex", flexDirection: "column" } },
                React.createElement(NavComponent, null),
                React.createElement(Button, { buttonStyle: "none", id: "clickable-reset-all", disabled: !filtersHook.isAnyFilterActive(), marginBottom0: true, onClick: filtersHook.handleClearFilters },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.resetAll" })),
                React.createElement(PeriodicalFilters, { filters: filtersHook.filters, libraryStatus: PeriodicalLibraryStatusEnum.SUBSCRIBED, publishers: publishers, isPublishersLoading: isPublishersLoading, selectedPublisher: selectedPublisher, onFilterChange: filtersHook.handleFilterChange, onFrequencyChange: filtersHook.handleFrequencyChange, onKultSubjectGroupOriginChange: filtersHook.handleKultSubjectGroupOriginChange, onStatusChange: filtersHook.handleStatusChange, onShowEndedChange: filtersHook.handleShowEndedChange, onProvisionalChange: filtersHook.handleProvisionalChange, onAgendaStateChange: activeLibraryId
                        ? filtersHook.handleAgendaStateChange
                        : undefined, onLastReceivedChange: filtersHook.handleLastReceivedChange, onPublisherInput: filtersHook.handlePublisherInput, onApplyFilters: filtersHook.handleApplyFilters, hasUnappliedChanges: filtersHook.hasUnappliedChanges() })))),
        React.createElement(Pane, { id: "contentPane", defaultWidth: "fill", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.navigation.periodicals",
            }), paneSub: dataHook.isError
                ? undefined
                : formatCountSubtitle(formatMessage, dataHook.totalRecords, dataHook.isCountCapped) },
            enforcementResult && (React.createElement(MessageBanner, { type: enforcementResult.type, dismissible: true, onExited: function () { return setEnforcementResult(null); }, "data-testid": "urgence-send-result" }, enforcementResult.text)),
            agendaResult && (React.createElement(MessageBanner, { type: "success", dismissible: true, onExited: function () { return setAgendaResult(null); }, "data-test-id": "agenda-change-result" }, agendaResult)),
            enforcementSkipped && (React.createElement(MessageBanner, { type: "warning", dismissible: true, onExited: function () { return setEnforcementSkipped(null); }, "data-testid": "urgence-skipped-summary" }, enforcementSkipped)),
            React.createElement(TableComponent, { data: dataHook.periodicalsData, columns: columns, columnWidths: COLUMN_WIDTHS, columnPrefsKey: "periodicals.management", pageSize: dataHook.pageSize, onPageSizeChange: dataHook.changePageSize, totalCount: dataHook.totalRecords, onNeedMoreData: handleNeedMoreData, searchTerm: "", filterPaneIsVisible: filterPaneIsVisible, toggleFilterPane: toggleFilterPane, source: source, emptyMessage: emptyMessage, isFetching: dataHook.isFetching, onRowClick: handleRowClick, sortBy: sort.sortBy, sortDir: sort.sortDir, onSort: handleSort, rowClassResolver: rowClassResolver, resetPaginationKey: "".concat(JSON.stringify(filtersHook.appliedFilters), "|").concat(sort.sortBy, "|").concat(sort.sortDir) })),
        selectedItem && (React.createElement(PeriodicalDetailPane, { key: selectedItem.id, periodicalLibrary: selectedItem, onClose: handleCloseDetail, renderActions: detailActionMenu })),
        selectedItem && kultSubjectGroupDialogOpen && (React.createElement(PeriodicalKultSubjectGroupDialog, { key: selectedItem.titleId, open: kultSubjectGroupDialogOpen, titleId: selectedItem.titleId, title: selectedItem.title, onClose: function () { return setKultSubjectGroupDialogOpen(false); } })),
        selectedItem && (React.createElement(ScheduleEditDialog, { open: scheduleDialogOpen, titleId: selectedItem.titleId, periodicalLibraryId: selectedItem.id, frequency: selectedItem.frequency, onClose: handleCloseSchedule })),
        checkSettings && (React.createElement(PeriodicalCheckSettingsDialog, { open: checkSettingsOpen, settings: checkSettings, onClose: function () { return setCheckSettingsOpen(false); }, onSaved: function () { return queryClient.invalidateQueries(["periodicalLibraries"]); } })),
        selectedItem && enforcementKind && (React.createElement(PeriodicalEnforcementDialog, { open: true, periodicalLibrary: selectedItem, enforcementKind: enforcementKind, onClose: handleCloseEnforcement, onResult: handleEnforcementResult })),
        React.createElement(ConfirmationModal, { open: endPubConfirmOpen, heading: formatMessage({
                id: "ui-br-legaldeposits.periodicals.action.end-publication",
            }), message: formatMessage({
                id: "ui-br-legaldeposits.periodicals.end-publication.confirm",
            }), onConfirm: handleConfirmEndPub, onCancel: handleCloseEndPub, isConfirmButtonDisabled: endPublicationMutation.isLoading }),
        React.createElement(ConfirmationModal, { open: reopenConfirmOpen, heading: formatMessage({
                id: "ui-br-legaldeposits.periodicals.action.reopen-publication",
            }), message: formatMessage({
                id: "ui-br-legaldeposits.periodicals.reopen-publication.confirm",
            }), onConfirm: handleConfirmReopen, onCancel: handleCloseReopen, isConfirmButtonDisabled: reopenPublicationMutation.isLoading }),
        React.createElement(TitleAgendaDialog, { open: agendaOverviewOpen && !!selectedItem, titleId: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.titleId, name: (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.title) !== null && _a !== void 0 ? _a : "", onClose: handleCloseAgendaOverview, actions: React.createElement(React.Fragment, null,
                titleAgendaQuery.data && (React.createElement(Button, { marginBottom0: true, onClick: function () { return setAgendaDialogMode("library"); }, "data-test-id": "periodical-action-agenda" },
                    React.createElement(FormattedMessage, { id: titleAgendaQuery.data.agenda === "PERIODICALS"
                            ? "ui-br-legaldeposits.agenda.action.toMonographs"
                            : "ui-br-legaldeposits.agenda.action.takeOver.PERIODICALS" }))),
                titleAgendaQuery.data && isNationalLibrary && (React.createElement(Button, { marginBottom0: true, onClick: function () { return setAgendaDialogMode("global"); }, "data-test-id": "periodical-action-global-type" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.action.fixGlobalType" })))) }),
        React.createElement(ChangeAgendaDialog, { open: agendaDialogMode !== null, mode: agendaDialogMode !== null && agendaDialogMode !== void 0 ? agendaDialogMode : "library", name: (_b = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.title) !== null && _b !== void 0 ? _b : "", titleAgenda: (_c = titleAgendaQuery.data) !== null && _c !== void 0 ? _c : null, onClose: handleCloseAgendaDialog, onChanged: handleAgendaChanged }),
        selectedItem && (React.createElement(GenerateVolumeDialog, { open: generateNextVolumeOpen, periodicalLibraryId: selectedItem.id, direction: "next", onClose: handleCloseGenerateNextVolume }))));
};
export default PeriodicalsManagementList;
